// Módulo Dominios — 4.2: "Gestión de subdominios y dominios personalizados"
// (alcance MVP: solo subdominio, ver api/dominios.js). El subdominio
// principal ya se crea automáticamente con la empresa; este módulo
// supervisa el estado DNS y permite corregir el valor si hiciera falta
// (ej. la empresa pidió cambiar de subdominio).

import { crearTabla, mostrarToast } from '@plataforma/ui';
import { esSubdominioValido } from '@plataforma/utils';

import { actualizarDominio, listarDominios } from '../api/dominios.js';
import { exigirSuperAdmin } from '../guardas.js';

import { montarShell } from './shell.js';

const TONO_ESTADO_DNS = { pendiente: 'alerta', verificado: 'exito', error: 'peligro' };
const ETIQUETA_ESTADO_DNS = { pendiente: 'Pendiente', verificado: 'Verificado', error: 'Error' };

export async function vistaDominios(contenedorRaiz) {
  if (!exigirSuperAdmin()) return;

  const contenido = montarShell(contenedorRaiz, '/dominios');
  contenido.innerHTML = `
    <h1 style="margin-bottom: 16px;">Dominios</h1>
    <div id="pf-dominios-tabla"><p class="pf-texto-suave">Cargando…</p></div>
  `;

  await cargarTabla(contenido);
}

async function cargarTabla(contenido) {
  const destino = contenido.querySelector('#pf-dominios-tabla');
  const { data, error } = await listarDominios();

  if (error) {
    destino.innerHTML = `<p class="pf-campo__error">No se pudieron cargar los dominios.</p>`;
    return;
  }

  const tabla = crearTabla({
    filaVacia: 'No hay dominios registrados.',
    columnas: [
      { titulo: 'Empresa', render: (fila) => fila.empresa?.nombre ?? '—' },
      { titulo: 'Dominio', render: (fila) => crearCampoValor(fila, () => cargarTabla(contenido)) },
      { clave: 'tipo', titulo: 'Tipo' },
      {
        titulo: 'DNS',
        render: (fila) => {
          const badge = document.createElement('pf-badge-estado');
          badge.setAttribute('tono', TONO_ESTADO_DNS[fila.estado_dns] ?? 'neutral');
          badge.textContent = ETIQUETA_ESTADO_DNS[fila.estado_dns] ?? fila.estado_dns;
          return badge;
        },
      },
      {
        titulo: 'Acciones',
        render: (fila) => crearAccionesDns(fila, () => cargarTabla(contenido)),
      },
    ],
    filas: data ?? [],
  });

  destino.innerHTML = '';
  destino.appendChild(tabla);
}

function crearCampoValor(dominio, alTerminar) {
  const envoltorio = document.createElement('div');
  envoltorio.className = 'pf-fila';

  const input = document.createElement('input');
  input.type = 'text';
  input.value = dominio.valor;
  input.style.width = '180px';

  const guardar = document.createElement('button');
  guardar.type = 'button';
  guardar.className = 'pf-boton pf-boton--secundario pf-boton--chico';
  guardar.textContent = 'Guardar';
  guardar.hidden = true;

  input.addEventListener('input', () => {
    guardar.hidden = input.value.trim().toLowerCase() === dominio.valor;
  });

  guardar.addEventListener('click', async () => {
    const nuevoValor = input.value.trim().toLowerCase();
    if (dominio.tipo === 'subdominio' && !esSubdominioValido(nuevoValor)) {
      mostrarToast('El subdominio no tiene un formato válido.', { tono: 'peligro' });
      return;
    }
    guardar.disabled = true;
    // Renombrar el subdominio principal vuelve a "pendiente" su estado DNS:
    // el registro anterior en Cloudflare ya no corresponde a este valor.
    const cambios =
      dominio.tipo === 'subdominio'
        ? { valor: nuevoValor, estado_dns: 'pendiente' }
        : { valor: nuevoValor };
    const { error } = await actualizarDominio(dominio.id, cambios);
    guardar.disabled = false;
    if (error) {
      mostrarToast('No se pudo actualizar el dominio.', { tono: 'peligro' });
      return;
    }
    mostrarToast('Dominio actualizado.', { tono: 'exito' });
    await alTerminar();
  });

  envoltorio.append(input, guardar);
  return envoltorio;
}

function crearAccionesDns(dominio, alTerminar) {
  const contenedor = document.createElement('div');
  contenedor.className = 'pf-fila';

  if (dominio.estado_dns !== 'verificado') {
    const botonVerificar = document.createElement('button');
    botonVerificar.type = 'button';
    botonVerificar.className = 'pf-boton pf-boton--primario pf-boton--chico';
    botonVerificar.textContent = 'Marcar verificado';
    botonVerificar.title = 'Confirma manualmente que el registro DNS ya propagó en Cloudflare.';
    botonVerificar.addEventListener('click', async () => {
      botonVerificar.disabled = true;
      const { error } = await actualizarDominio(dominio.id, { estado_dns: 'verificado' });
      if (error) {
        mostrarToast('No se pudo actualizar el estado DNS.', { tono: 'peligro' });
        botonVerificar.disabled = false;
        return;
      }
      mostrarToast('Dominio marcado como verificado.', { tono: 'exito' });
      await alTerminar();
    });
    contenedor.appendChild(botonVerificar);
  }

  return contenedor;
}
